import { useCallback, useId, type ReactNode } from 'react';
import { Icon } from '../Icon/Icon';
import { BARCODE_MAX_SIDE, downscaleImage } from '../../lib/image';
import './scanzone.css';

/** What the document reader accepts (architecture.md §8): jpg, png, webp, heic, pdf. */
export const DOCUMENT_ACCEPT =
  'image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif,application/pdf';

export interface FileTriggerProps {
  accept?: string;
  /**
   * A photo larger than this on its long side goes up as a JPEG of this size (lib/image).
   * The default keeps enough for a boarding pass's barcode; ID photos pass DOCUMENT_MAX_SIDE.
   */
  maxSide?: number;
  /** The picked file, made smaller first when it's a big photo (PDFs and small files as they are). */
  onFile: (file: File) => void;
  /** Render prop: call `open()` from your own button (S13's primary "Take a photo or upload"). */
  children: (open: () => void) => ReactNode;
}

/**
 * A hidden file input opened by any control. On phones the picker offers the camera too,
 * so there's no `capture` attribute: that would force the camera and hide screenshots.
 */
export function FileTrigger({
  accept = DOCUMENT_ACCEPT,
  maxSide = BARCODE_MAX_SIDE,
  onFile,
  children,
}: FileTriggerProps) {
  const id = useId();
  // Looked up by id, not a ref: `open` is handed out during render (react-hooks/refs).
  const open = useCallback(() => document.getElementById(id)?.click(), [id]);
  return (
    <>
      {children(open)}
      <input
        id={id}
        type="file"
        accept={accept}
        hidden
        data-testid="file-input"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = ''; // the same file can be picked again after a failure
          if (f) void downscaleImage(f, { maxSide }).then(onFile);
        }}
      />
    </>
  );
}

export function Spinner() {
  return <span className="spinner" aria-hidden="true" />;
}

export interface ScanZoneProps {
  /** Idle: "Point your camera at the barcode". */
  title: ReactNode;
  /** Idle: "or upload a photo, screenshot or PDF". */
  sub?: ReactNode;
  /** Busy state: spinner + busyTitle ("Reading your boarding pass…"). */
  busy?: boolean;
  busyTitle?: ReactNode;
  busySub?: ReactNode;
  accept?: string;
  maxSide?: number;
  onFile: (file: File) => void;
}

/** The dashed drop target that opens the camera / file picker, with its busy state. */
export function ScanZone({ title, sub, busy, busyTitle, busySub, accept, maxSide, onFile }: ScanZoneProps) {
  if (busy)
    return (
      <div className="scanzone scanzone--busy" role="status" aria-live="polite" aria-busy="true">
        <Spinner />
        <span className="scanzone__t">{busyTitle}</span>
        {busySub && <span className="scanzone__s">{busySub}</span>}
      </div>
    );
  return (
    <FileTrigger accept={accept} maxSide={maxSide} onFile={onFile}>
      {(open) => (
        <button type="button" className="scanzone" onClick={open}>
          <Icon name="scan" />
          <span className="scanzone__t">{title}</span>
          {sub && <span className="scanzone__s">{sub}</span>}
        </button>
      )}
    </FileTrigger>
  );
}
